/* The arborist's work on the phone until it syncs (`27` section 6, `02` ->
 * Offline work is kept on the phone, and the token is not): audits,
 * corrections, missed trees and their photos, in IndexedDB, so they survive a
 * closed app and no signal.
 *
 * Each piece of work is one record under an ID generated on the phone, which
 * becomes its `field_audit.id` (`20` section 3.12b), so a resend whose audit
 * already exists writes nothing. Saving the same tree again before it syncs
 * replaces the record and keeps the ID. Nothing here says "synced": synced work
 * is removed from the phone, so everything held here is unsynced.
 *
 * What it deliberately is not: the sync, which is AR-338's route and removes
 * what it sent. Not `localStorage`, which cannot take a photo. Not encrypted:
 * the phone is a managed device instead (`02`).
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";

const DATABASE = "arborcision-field";
const VERSION = 1;
const STORE = "unsynced";

export type AuditStatus = "validated" | "corrected" | "missed";

export interface Point {
  lat: number;
  lng: number;
}

/* A position as the phone's GPS reported it, with the accuracy it claimed. */
export interface Fix extends Point {
  accuracyM: number | null;
}

/* An arborist's photo, with when and where it was taken: the server stores
 * each (`20` section 3.12b, `field_audit_photo`). */
export interface FieldPhoto {
  blob: Blob;
  takenAt: number;
  location: Fix | null;
}

/* What the arborist recorded about a tree, as the field app's screens set it.
 * A value is present where they changed it; absent agrees with the system.
 * Distances are feet, as the screens set them; the API takes metres (`15`
 * section 1.6), so a sync converts them with lib/format.ts's feetToMeters.
 * No risk: the server scores a tree's, never the phone. No contact flag:
 * contact with the conductor is a clearance of 0 (`27` section 8). */
export interface AuditValues {
  status: AuditStatus;
  species?: string;
  clearance?: number;
  encroach?: number;
  reasons?: string[];
  note?: string;
  repin?: Point;
}

/* A tree the system missed, as the arborist recorded it. It has no row of
 * ours to point at, so its audit carries it whole (`27` section 8). Its
 * height and distances are feet, as AuditValues' are. */
export interface MissedTree extends Point {
  /* The label the app shows for it on this phone, not an ID anywhere else. */
  id: string;
  site: string;
  pole: string;
  species: string;
  height: number;
  clearance: number;
  encroach: number;
  photos: FieldPhoto[];
}

export interface UnsyncedAudit {
  /* Generated on the phone; the audit's ID on the server too. */
  id: string;
  /* The tree as the app keys it: our tree's or held missed tree's ID, or
   * the label of a missed tree recorded here (trees.ts's FieldTree `id`). */
  treeKey: string;
  /* The assignment the tree came to the phone with, from the open list
   * (`15` section 3.15, AR-343). The server requires it on every audit but
   * the one that records a missed tree, which has none (`20` section 3.12b):
   * a verdict on a held missed tree carries its assignment as a tree's does. */
  assignmentId: string | null;
  recordedAt: number;
  audit: AuditValues;
  /* Where the arborist stood, from the fix taken when the audit is saved
   * (`27` section 8, AR-343). The server requires it (`20` section 3.12b),
   * so a record without one is not sent: null is never replaced by a guess. */
  stoodAt: Fix | null;
  /* The arborist's photos of the tree, kept as taken. */
  photos: FieldPhoto[];
  missedTree: MissedTree | null;
}

/* What saving a tree writes: its record on the phone with `change` applied,
 * or a new record where it has none. The ID, assignment, fix, photos and
 * missed tree carry over unless `change` sets them, so a tree saved again
 * before it syncs keeps the ID that becomes its audit's ID on the server
 * (`20` section 3.12b). `change` is given the existing record to merge into. */
export function recordFor(
  records: readonly UnsyncedAudit[],
  treeKey: string,
  change: (existing: UnsyncedAudit | undefined) => Pick<UnsyncedAudit, "audit"> & Partial<UnsyncedAudit>,
): UnsyncedAudit {
  const existing = records.find((r) => r.treeKey === treeKey);
  return {
    id: existing?.id ?? crypto.randomUUID(),
    treeKey,
    assignmentId: existing?.assignmentId ?? null,
    recordedAt: Date.now(),
    stoodAt: existing?.stoodAt ?? null,
    photos: existing?.photos ?? [],
    missedTree: existing?.missedTree ?? null,
    ...change(existing),
  };
}

/* The audit a save writes: the verdict on screen, never one saved before it.
 * A validation clears an earlier correction, and a correction replaces one,
 * keeping only a pin moved on the map, which the correction form does not
 * show. A missed tree's save merges into what its audit holds, so an edit of
 * its values on the tree screen, or a moved pin, lands on the audit: a shape
 * the sync route refuses for a missed outcome (`15` section 3.15a) until the
 * missed-tree form writes them to its MissedTree instead (AR-343). */
export function verdict(prior: AuditValues | undefined, values: AuditValues): AuditValues {
  if (values.status === "validated") return values;
  if (values.status === "corrected") return prior?.repin ? { ...values, repin: prior.repin } : values;
  return { ...prior, ...values };
}

export interface UnsyncedStore {
  /* Every record, oldest first. */
  all(): Promise<UnsyncedAudit[]>;
  /* Resolves once the record is on disk, and rejects where it is not. */
  put(record: UnsyncedAudit): Promise<void>;
}

function result<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/* Exported for its test: no record `put` accepts makes a write fail once it
 * has begun, so the failure is caused on a transaction of the test's own. */
export function committed(transaction: IDBTransaction): Promise<void> {
  // Abort, not error: a failed request's error reaches the transaction before
  // `transaction.error` is set, and then aborts it with that error.
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error ?? new Error("the write was abandoned"));
  });
}

/* `factory` is the browser's `indexedDB`, and a test's own. */
export async function openUnsynced(factory: IDBFactory): Promise<UnsyncedStore> {
  const request = factory.open(DATABASE, VERSION);
  request.onupgradeneeded = () => {
    request.result.createObjectStore(STORE, { keyPath: "id" });
  };
  const db = await result(request);
  // A newer version of the app, opened in another tab or installed by the
  // service worker, cannot upgrade the database while this page holds it open.
  db.onversionchange = () => db.close();
  return {
    async all() {
      const records = await result(
        db.transaction(STORE).objectStore(STORE).getAll() as IDBRequest<UnsyncedAudit[]>,
      );
      return records.sort((a, b) => a.recordedAt - b.recordedAt);
    },
    async put(record) {
      // Strict, so a write is reported done only once it is on disk: this is
      // the only copy of the arborist's work, and a phone can die mid-day.
      const transaction = db.transaction(STORE, "readwrite", { durability: "strict" });
      transaction.objectStore(STORE).put(record);
      await committed(transaction);
    },
  };
}

/* Asks the browser to keep this origin's storage when the phone runs short of
 * space. It does not lift WebKit's deletion of storage a site has not used;
 * installing to the home screen does (`27` section 6). Refused or unsupported
 * is not an error: the work is stored either way. */
export async function keepStorage(storage: StorageManager | undefined): Promise<boolean> {
  if (!storage?.persist) return false;
  try {
    return (await storage.persisted()) || (await storage.persist());
  } catch {
    return false;
  }
}

let opened: Promise<UnsyncedStore> | null = null;

/* One connection for the page. A failed open is forgotten, so the next read
 * tries again rather than failing forever. */
function store(): Promise<UnsyncedStore> {
  if (!opened) {
    void keepStorage(navigator.storage);
    opened = openUnsynced(indexedDB).catch((error: unknown) => {
      opened = null;
      throw error;
    });
  }
  return opened;
}

const UNSYNCED = ["field", "unsynced"] as const;

/* The work on this phone, and saving to it. `networkMode: "always"` because
 * TanStack Query pauses a query while the phone is offline, which is exactly
 * when this one must run. A save updates the cache with the record it wrote
 * rather than reading the store again, so each photo keeps one Blob, and one
 * object URL (`photoUrl`), for the life of the page. */
export function useUnsyncedWork() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: UNSYNCED,
    queryFn: async () => (await store()).all(),
    networkMode: "always",
    staleTime: Infinity,
  });
  const save = async (record: UnsyncedAudit): Promise<void> => {
    await (await store()).put(record);
    queryClient.setQueryData<UnsyncedAudit[]>(UNSYNCED, (records = []) =>
      records.some((r) => r.id === record.id)
        ? records.map((r) => (r.id === record.id ? record : r))
        : [...records, record],
    );
  };
  return { work: query.data, error: query.error, save };
}

/* A photo is a record on disk and an object URL on screen, and the screens
 * pass the URL around. These map one to the other. A URL is never revoked:
 * its photo is on the phone until it syncs, and the page outlives none of it. */
const urls = new WeakMap<FieldPhoto, string>();
const photos = new Map<string, FieldPhoto>();

export function photoUrl(photo: FieldPhoto): string {
  let url = urls.get(photo);
  if (!url) {
    url = URL.createObjectURL(photo.blob);
    urls.set(photo, url);
    photos.set(url, photo);
  }
  return url;
}

/* The photo behind a URL `photoUrl` made. Any other URL is a photo this page
 * cannot store, and saving it as nothing would lose it without a word. */
export function heldPhoto(url: string): FieldPhoto {
  const photo = photos.get(url);
  if (!photo) throw new Error(`no photo is held for ${url}`);
  return photo;
}
